/**
 * Firebase Analytics for the CurrencyCore customer dashboard.
 * Measurement ID: G-SZ4PTQYR8D
 *
 * All calls are silently no-ops when Analytics is unavailable
 * (SSR, ad-blocker, test environment).
 */
import { initializeApp, getApps } from "firebase/app";
import {
  getAnalytics,
  logEvent,
  setUserId,
  setUserProperties,
  type Analytics,
} from "firebase/analytics";

const FIREBASE_CONFIG = {
  apiKey:            "AIzaSyBuPKGvdKy5uwDlmUXw2zZT0bEbdyLyjXY",
  authDomain:        "currencycore.firebaseapp.com",
  projectId:         "currencycore",
  storageBucket:     "currencycore.firebasestorage.app",
  messagingSenderId: "893147115568",
  appId:             "1:893147115568:web:1b7726cfe40be58beb7df5",
  measurementId:     "G-SZ4PTQYR8D",
};

let _analytics: Analytics | null = null;

/** Initialise once. Safe to call multiple times. */
export function initAnalytics(): Analytics | null {
  if (typeof window === "undefined") return null; // SSR guard
  try {
    const app = getApps().length === 0
      ? initializeApp(FIREBASE_CONFIG)
      : getApps()[0]!;
    _analytics = getAnalytics(app);
    return _analytics;
  } catch {
    return null;
  }
}

function ga(): Analytics | null {
  return _analytics;
}

// ---------------------------------------------------------------------------
// User identity — call once after login / org selection.
// ---------------------------------------------------------------------------

/** Attach user identity to every subsequent event. */
export function setAnalyticsUser(userId: string | null, orgId?: string | null) {
  const a = ga();
  if (!a) return;
  try {
    if (userId) setUserId(a, userId);
    setUserProperties(a, {
      org_id:  orgId  ?? null,
      user_id: userId ?? null,
    });
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Generic event helper.
// ---------------------------------------------------------------------------

export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean | null>,
) {
  const a = ga();
  if (!a) return;
  try { logEvent(a, eventName, params ?? {}); } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Page tracking.
// ---------------------------------------------------------------------------

export function trackPageView(pagePath: string, pageTitle?: string) {
  trackEvent("page_view", {
    page_path:  pagePath,
    page_title: pageTitle ?? document.title,
  });
}

// ---------------------------------------------------------------------------
// Dashboard-specific events.
// ---------------------------------------------------------------------------

export function trackPlanView(planTier: string) {
  trackEvent("plan_view", { plan_tier: planTier });
}

export function trackPlanUpgradeStart(fromTier: string, toTier: string) {
  trackEvent("plan_upgrade_start", { from_tier: fromTier, to_tier: toTier });
}

export function trackApiKeyAction(action: "create" | "revoke" | "copy") {
  trackEvent("api_key_action", { action });
}

export function trackByokSave(providerCount: number) {
  trackEvent("byok_save", { provider_count: providerCount });
}

export function trackAiCreditPurchase(packageId: string, credits: number) {
  trackEvent("ai_credit_purchase", { package_id: packageId, credits });
}

export function trackUsageView(period: string) {
  trackEvent("usage_view", { period });
}

// ---------------------------------------------------------------------------
// Auth events
// ---------------------------------------------------------------------------

export function trackLogin(method: "email" | "google" | "sso") {
  trackEvent("login", { method });
}

export function trackSignup(method: "email" | "google") {
  trackEvent("signup", { method });
}

// ---------------------------------------------------------------------------
// API key events
// ---------------------------------------------------------------------------

export function trackApiKeyRotate() {
  trackEvent("api_key_action", { action: "rotate" });
}

// ---------------------------------------------------------------------------
// BYOK events
// ---------------------------------------------------------------------------

export function trackByokToggle(enabled: boolean) {
  trackEvent("byok_toggle", { enabled: enabled ? 1 : 0 });
}

export function trackByokDelete() {
  trackEvent("byok_delete", {});
}

// ---------------------------------------------------------------------------
// Org events
// ---------------------------------------------------------------------------

export function trackOrgSwitch(toOrgId: string) {
  trackEvent("org_switch", { to_org_id: toOrgId });
}

export function trackOrgCreate() {
  trackEvent("org_create", {});
}

// ---------------------------------------------------------------------------
// Playground
// ---------------------------------------------------------------------------

export function trackPlaygroundQuery(endpointId: string, statusCode: number, durationMs: number) {
  trackEvent("playground_query", {
    endpoint_id:  endpointId,
    status_code:  statusCode,
    duration_ms:  durationMs,
    success:      statusCode >= 200 && statusCode < 300 ? 1 : 0,
  });
}
